import React from "react";
import filter from "../../filter";
import ListStats from "../ListStats";
import ListTable from "../ListTable";
import ListResults from "../ListResults";
import Print from "../Print";
import shallowequal from "shallowequal";
import Alert from "../Alert";
import { executeQuery } from "../../requests/executeQuery";
import { executeBackendQuery } from "../../requests/executeBackendQuery";
import { manageData } from "../../requests/CRUD";
import { PROPERTIES, NEW_DEVELOPMENT, HOT_LIST, NEED_OWNER, TEAM, TERRITORIES_V2, MARKETS } from "../../consts/tables";
import { getData } from "../../requests/CRUD";
import {
  V9_PROPERTIES_LIST,
  V7_PROPERTIES_LIST,
  V7_PROPERTIES_STATS,
  V7_PROPERTIES_STATS_ACTIVE,
  V7_PROPERTIES_STATS_COUNT,
  V9_PROPERTIES_STATS,
  V9_PROPERTIES_STATS_ACTIVE,
  V9_PROPERTIES_STATS_COUNT,
  V10_PROPERTIES_STATS,
  V10_PROPERTIES_STATS_COUNT,
  V10_PROPERTIES_LIST,
  V7_TERRITORY_FLAG_STATS,
  V9_TERRITORY_FLAG_STATS,
  V10_TERRITORY_FLAG_STATS,
  V9_HOT_LIST_COUNT,
  V10_HOT_LIST_COUNT,
  GET_ALL_PRODUCT_TYPES,
  INVESTOR_HOT_LIST_COUNT,
} from "../../consts/queries";
import LinkComp from "../LinkComp";
import hscTeamMembers from "../../hsc-team-members";
import Icon from "../Icon";
import plus from "../../svgs/plus-white.svg";
import trash from "../../svgs/Trash.svg";
import Dropdown from "../Dropdown/Dropdown";
import ConfirmationDialog from "../../components/ConfirmDialog";
import BulkEditProperties from "../BulkEditProperties";
import FindDuplicateProperties from "../FindDuplicateProperties";
import MergeProperties, { MERGE_MAX_PROPERTIES, mergeSummary } from "../FindDuplicateProperties/MergeProperties";
import FilterButton from "../FilterButton";
import { browserHistory } from "react-router";
import { normalizeListQuery, parcelMapQuery, preprocessQueryFilters, writeNarrowedHandoff, readNarrowedHandoff, clearNarrowedHandoff, writeMailMergeSelection } from "../../utils";
import { dateUStoISO, toISODateOnly } from "../../date-converter";
import { can, getCurrentAccess } from "../../access";
import { ownerResearchApi } from "../../requests/ownerResearch";
import { editsEverySummitRecord, editsOwnSummitDealsOnly } from "../../access-levels";

let cloneDeep = require("lodash.clonedeep");
const limit = 100;

/** Mirrors MailMergeService::MAX_PROPERTIES — refused here so the user is told why. */
const MAIL_MERGE_MAX_PROPERTIES = 5000;

/**
 * The most rows one filter hands over at once (see fetchFullFilteredSet).
 *
 * Quoted in the "select everything matching" offer so the number on the button
 * is the number that ends up selected.
 */
const FULL_SET_LIMIT = 9999;

/**
 * How long the Sale Comps print waits for property photos before printing anyway.
 */
const PRINT_IMAGE_TIMEOUT_MS = 10000;

/**
 * Resolves once every photo in the print sheet has loaded or failed. The print
 * dialog snapshots the page as-is, so printing straight after render leaves the
 * Photo column blank.
 */
function waitForPrintImages() {
  const pending = Array.from(document.querySelectorAll(".Print img"))
    .filter((img) => !img.complete)
    .map((img) => new Promise((resolve) => {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
    }));
  const timeout = new Promise((resolve) => setTimeout(resolve, PRINT_IMAGE_TIMEOUT_MS));
  return Promise.race([Promise.all(pending), timeout]);
}

/**
 * Prints with a landscape page, for the Sale Comps sheet's wide table.
 *
 * The rule goes in at both ends of the head: among competing !important @page
 * sizes Chromium honours the first while spec-correct engines honour the last
 * (see PrintControls.js). It is removed once the dialog closes so other prints
 * keep their own orientation.
 */
function printLandscape() {
  const css = "@page { size: landscape !important; }";
  const first = document.createElement("style");
  first.innerHTML = css;
  const last = document.createElement("style");
  last.innerHTML = css;
  document.head.insertBefore(first, document.head.firstChild);
  document.head.appendChild(last);
  try {
    print();
  } finally {
    first.remove();
    last.remove();
  }
}

// Compare by agent last name then first name (same logic as single-sort AGENT column)
function compareAgentName(a, b, direction) {
  const nameA = a.AGENT_1_LAST_NAME || a.AGENT_2_LAST_NAME || a.AGENT_3_LAST_NAME || "";
  const nameB = b.AGENT_1_LAST_NAME || b.AGENT_2_LAST_NAME || b.AGENT_3_LAST_NAME || "";
  let cmp = nameA.localeCompare(nameB);
  if (cmp === 0) {
    if (!nameA) {
      // Within the "no named agent" group, keep truly-blank before OA
      const oaA = a.AGENT_4_LAST_NAME ? 1 : 0;
      const oaB = b.AGENT_4_LAST_NAME ? 1 : 0;
      return direction === "desc" ? oaB - oaA : oaA - oaB;
    }
    const fnA = a.AGENT_1_FIRST_NAME || a.AGENT_2_FIRST_NAME || a.AGENT_3_FIRST_NAME || "";
    const fnB = b.AGENT_1_FIRST_NAME || b.AGENT_2_FIRST_NAME || b.AGENT_3_FIRST_NAME || "";
    cmp = fnA.localeCompare(fnB);
  }
  return direction === "desc" ? -cmp : cmp;
}

function compareOwnerName(a, b, direction) {
  const nameA = a.CURRENT_OWNER_NAME || "";
  const nameB = b.CURRENT_OWNER_NAME || "";
  // Keep properties without an owner at the end for both directions.
  if (!nameA && !nameB) return 0;
  if (!nameA) return 1;
  if (!nameB) return -1;
  const cmp = String(nameA).localeCompare(String(nameB), "en", { numeric: true });
  return direction === "desc" ? -cmp : cmp;
}

// A territory list's server sort goes by flag, then status, with no flag and no
// status last both ways (filter.js); a page re-sort must agree.
function compareStatusByFlag(a, b, direction) {
  const sign = direction === "desc" ? -1 : 1;
  const noFlag = (row) => (row.STATUS_FLAG_NONE ? 1 : 0);
  if (noFlag(a) !== noFlag(b)) return noFlag(a) - noFlag(b);
  const rank = (Number(a.STATUS_FLAG_RANK) || 0) - (Number(b.STATUS_FLAG_RANK) || 0);
  if (rank !== 0) return sign * rank;
  const statusA = a.STATUS || "";
  const statusB = b.STATUS || "";
  if (!statusA && !statusB) return 0;
  if (!statusA) return 1;
  if (!statusB) return -1;
  return sign * statusA.localeCompare(statusB);
}

// Street name first (a leading house number is dropped), then house number numerically, then the raw string.
// Empty addresses sort first on asc / last on desc, matching MySQL NULL ordering.
function compareAddress(a, b, direction) {
  const addrA = String(a.ADDRESS != null ? a.ADDRESS : "").trim();
  const addrB = String(b.ADDRESS != null ? b.ADDRESS : "").trim();
  let cmp = 0;
  if (!addrA || !addrB) {
    cmp = (addrA ? 1 : 0) - (addrB ? 1 : 0);
  } else {
    const splitAddress = (addr) => {
      const m = addr.match(/^(\d+)\S*\s*(.*)$/);
      return m ? { num: parseInt(m[1], 10), street: m[2] } : { num: 0, street: addr };
    };
    const pa = splitAddress(addrA);
    const pb = splitAddress(addrB);
    cmp = pa.street.localeCompare(pb.street, "en", { sensitivity: "base" });
    if (cmp === 0) cmp = pa.num - pb.num;
    if (cmp === 0) cmp = addrA.localeCompare(addrB, "en", { numeric: true });
  }
  return direction === "desc" ? -cmp : cmp;
}

const DATE_COLUMNS = new Set([
  'CLOSE_OF_ESCROW', 'EFFECTIVE_DATE', 'LISTED_DATE', 'LEASE_COMMENCEMENT_DATE',
  'LEASE_EXPIRATION_DATE', 'LAST_MODIFIED_DATE', 'LOAN_CLOSING_DATE', 'FEATURED_DATE',
  'TRANS_DATE', 'DATE_CREATED', 'PROPOSAL_DATE', 'MATURITY_DATE', 'COURTING_SINCE',
]);


function compareDates(a, b) {
  const da = a ? new Date(a) : null;
  const db = b ? new Date(b) : null;
  const validA = da && !isNaN(da.getTime());
  const validB = db && !isNaN(db.getTime());
  if (!validA && !validB) return 0;
  if (!validA) return -1;
  if (!validB) return 1;
  return da.getTime() - db.getTime();
}

// Sort items by secondary order specs (array of { code, direction }), e.g. [{ code: "AGENT_1_LAST_NAME", direction: "asc" }]
function sortBySecondaryOrder(items, orderSpecs, statusByFlag = false) {
  if (!orderSpecs || orderSpecs.length === 0) return items;
  const sorted = [...items];
  sorted.sort((a, b) => {
    for (let i = 0; i < orderSpecs.length; i++) {
      const { code, direction } = orderSpecs[i];
      let cmp = 0;
      if (code === "STATUS" && statusByFlag) {
        cmp = compareStatusByFlag(a, b, direction);
      } else if (code === "AGENT_1_LAST_NAME") {
        cmp = compareAgentName(a, b, direction);
      } else if (code === "CURRENT_OWNER_NAME") {
        cmp = compareOwnerName(a, b, direction);
      } else if (code === "ADDRESS") {
        cmp = compareAddress(a, b, direction);
      } else if (DATE_COLUMNS.has(code)) {
        cmp = compareDates(a[code], b[code]);
        if (direction === "desc") cmp = -cmp;
      } else {
        const valA = a[code] != null ? a[code] : "";
        const valB = b[code] != null ? b[code] : "";
        if (typeof valA === "number" && typeof valB === "number") {
          cmp = valA - valB;
        } else {
          cmp = String(valA).localeCompare(String(valB), "en", { numeric: true });
        }
        if (direction === "desc") cmp = -cmp;
      }
      if (cmp !== 0) return cmp;
    }
    return 0;
  });
  return sorted;
}

export default class Listing extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      showConfirm: false,
      uniquePropCount: 0,
      originalItems: [],
      totalCount: 0,
      stats: null,
      loadingNewList: false,
      error: null,
      isBusy: null,
      query: normalizeListQuery(this.props.location.query),
      hscquery: {},
      underwritingSuccess: false,
      underwritingError: false,
      selectedNo: 0,
      unselectedNo: 0,
      areRemovedItems: false,
      selectionStats: false,
      comps: "",
      saleCompItems: null,
      selectedAll: false,
      lastVisited: "",
      newDevelopmentPropertyIds: new Set(),
      hotListPropertyIds: new Set(),
      needOwnerPropertyIds: new Set(),
      territoryFlagCounts: null,
      ownerResearchReviews: null,
      investorHotListCount: 0,
      agentName: null,
      territoryName: null,
      productTypeName: null,
      marketName: null,
      initialAgentQuery: null,
      bulkEditOpen: false,
      bulkEditResult: null,
      findDupsOpen: false,
      mergeOpen: false,
      // Snapshot taken when Merge opens: the popup resets whenever this array
      // changes identity.
      mergeProperties: [],
      mergeResult: null,
      // The ticked rows, by row id, kept across page steps and re-sorts — the
      // list only ever holds one page of rows, so a selection that lived on
      // them could not outlive a step to page 2. Values are the rows
      // themselves, because Delete Selected and Mail Merge want the row and not
      // just its id.
      selectedById: new Map(),
      selectingAllMatching: false,
    };

    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.removeSelected = this.removeSelected.bind(this);
    this.removeUnselected = this.removeUnselected.bind(this);
    this.reset = this.reset.bind(this);
    this.updateStats = this.updateStats.bind(this);
    this.deleteSelected = this.deleteSelected.bind(this);
    this.handleChangeDropdown = this.handleChangeDropdown.bind(this);
    this.onCloseConfirm = this.onCloseConfirm.bind(this);
    this.selectSaleComps = this.selectSaleComps.bind(this);
    this.startMailMerge = this.startMailMerge.bind(this);
    this.selectAllMatching = this.selectAllMatching.bind(this);
    this.clearSelection = this.clearSelection.bind(this);
    this.openBulkEdit = this.openBulkEdit.bind(this);
    this.closeBulkEdit = this.closeBulkEdit.bind(this);
    this.onBulkEditSaved = this.onBulkEditSaved.bind(this);
    this.openFindDups = this.openFindDups.bind(this);
    this.closeFindDups = this.closeFindDups.bind(this);
    this.openMerge = this.openMerge.bind(this);
    this.closeMerge = this.closeMerge.bind(this);
    this.onMerged = this.onMerged.bind(this);
    this.fetchFullFilteredSet = this.fetchFullFilteredSet.bind(this);
    // Find Dups must know it got the whole list, and asks in small pages so
    // a big filter does not exhaust the server's memory limit.
    this.fetchFilteredSetForDups = () =>
      this.fetchFullFilteredSet({ strict: true, pageSize: 500, showBusy: false });
    this.afterPrint = this.afterPrint.bind(this);
    this.listingRef = React.createRef();
    this.headerRef = React.createRef();
    this.updateHeaderOffset = this.updateHeaderOffset.bind(this);
    // Which filter the selection belongs to; a new one empties it, a page step
    // or a re-sort does not.
    this._selectionSignature = null;
    this._shouldRestoreListScroll = false;
    this._pinnedInitialScroll = false;
    this._prevScrollRestoration = null;
  }

  // The sticky page header (stats boxes + title + filter) changes height as the stats
  // boxes wrap/change, so a fixed rem offset for the sticky table header goes out of
  // alignment. Measure the header height and expose it as a CSS variable, so the table
  // header always sits flush below it (see Listing.scss). Scoped to this page only.
  updateHeaderOffset() {
    const header = this.headerRef.current;
    const root = this.listingRef.current;
    if (!header || !root) return;
    root.style.setProperty("--properties-header-offset", header.offsetHeight + "px");
  }

  componentDidMount() {
    let queries = normalizeListQuery(this.props.location.query);
    // Restore scroll only when returning from a property page. A plain /list open
    // (or async Owner-cell re-renders) should stay at the top.
    const currentListSearch = this.props.location.search || window.location.search || "";
    const lastClickedListSearch = sessionStorage.getItem("lastClickedListSearch") || "";
    const shouldRestoreScroll =
      sessionStorage.getItem("restoreListScroll") === "1" &&
      lastClickedListSearch === currentListSearch;
    sessionStorage.removeItem("restoreListScroll");
    sessionStorage.removeItem("lastClickedListSearch");
    let lastVisited = shouldRestoreScroll
      ? sessionStorage.getItem("lastClickedProperty")
      : null;
    if (!shouldRestoreScroll) {
      sessionStorage.removeItem("lastClickedProperty");
    }
    this._shouldRestoreListScroll = shouldRestoreScroll;
    this.setState({ lastVisited });

    // Browser scroll restoration + async row height changes (Owner cells) can leave
    // Peak/HSC closed lists mid-page on first paint. Pin to top unless restoring.
    if (typeof history !== "undefined" && "scrollRestoration" in history) {
      this._prevScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = "manual";
    }
    if (!shouldRestoreScroll) {
      window.scrollTo(0, 0);
    }

    // Coming back from the map view with a Remove Selected / Remove Unselected
    // still active: fetch normally (so stats and originalItems stay complete)
    // and re-apply the narrowing to the result — see fetch().
    this._restoreNarrowed = readNarrowedHandoff();

    this.fetch(queries);

    // Stagger subsequent requests to avoid 504 Gateway Time-out
    // Arcimedes batches these requests into a single HTTP call, which can exceed nginx timeouts
    setTimeout(() => {
      this.fetchNewDevelopmentIds();
      this.fetchHotListIds();
      this.fetchNeedOwnerIds();
      this.fetchInvestorHotListCount();
      this.initAgentFilter(this.props.location.query);
    }, 100);

    window.history.replaceState(null, "");

    // Keep the sticky table header aligned to the (variable-height) page header.
    this.updateHeaderOffset();
    window.addEventListener("resize", this.updateHeaderOffset);
    if (typeof ResizeObserver !== "undefined" && this.headerRef.current) {
      this._headerResizeObserver = new ResizeObserver(() => this.updateHeaderOffset());
      this._headerResizeObserver.observe(this.headerRef.current);
    }

    let hscquery = {
      HSC_TEAM_MEMBERS: "",
    };
    let member =
      (queries.HSC_TEAM_MEMBERS && queries.HSC_TEAM_MEMBERS.split(",")) ||
      [];
    const memberList = hscTeamMembers.filter((e) => {
      return member.indexOf(e.EMAIL) >= 0;
    });
    memberList.map((el, index) => {
      hscquery.HSC_TEAM_MEMBERS += el
        ? index === hscTeamMembers.length - 1
          ? el.EMAIL
          : el.EMAIL + ","
        : "";
    });
    this.setState({ hscquery });
  }

  /**
   * Identity of the filter set the summary header was computed from.
   *
   * `order` and `page` are excluded on purpose — they are exactly the parts a
   * column-header click or a page step changes, and neither affects an aggregate.
   * The chosen query names are included because the same filters can resolve to
   * the V7, V9 or V10 family, and those return different figures.
   */
  static aggregateSignature(query, page, ...queryNames) {
    const filters = { ...query };
    delete filters.order;
    delete filters.page;
    const keys = Object.keys(filters).sort();
    return JSON.stringify([page, queryNames, keys.map((k) => [k, filters[k]])]);
  }

  /**
   * Identity of the rows the current selection was made against.
   *
   * `page` and `order` are left out on purpose: stepping through the pages or
   * re-sorting shows different rows but the same list, and a selection that
   * emptied itself on either would be no use for editing more than one page.
   * Any other change is a different list, and the ticks go with it.
   */
  static selectionSignature(query) {
    const filters = { ...query };
    delete filters.page;
    delete filters.order;
    const keys = Object.keys(filters).sort();
    return JSON.stringify(keys.map((k) => [k, filters[k]]));
  }

  static getOptions(query, page) {
    // When multiple sort columns are specified, only send the primary (first)
    // sort column to the server. Arcimedes ORDER BY uses fixed column positions
    // so multi-column sorts ignore the user's click order. Sending only the
    // primary sort ensures correct pagination; secondary sorts are applied
    // client-side after data returns.
    let serverQuery = query;
    if (query.order) {
      const orderArr = Array.isArray(query.order)
        ? query.order
        : String(query.order).split(',').map((s) => s.trim()).filter(Boolean);
      if (orderArr.length > 1) {
        serverQuery = { ...query, order: orderArr[0] };
      }
    }
    const options = filter(serverQuery, page);
    options.param.limit = limit;
    options.param.offset = query.page ? (Number(query.page) - 1) * limit : 0;
    return options;
  }

  static setQueryOrder(q) {
    const query = { ...q };
    if (
      query.SOURCE &&
      query.SOURCE.toLowerCase() === "strive" &&
      ((query.STATUS && query.STATUS.toLowerCase() === "closed") ||
        (query.HSC_STATUS && query.HSC_STATUS.toLowerCase() === "closed"))
    ) {
      if (
        Array.isArray(query.order) &&
        query.order.indexOf("CLOSE_OF_ESCROW.desc") === -1
      ) {
        query.order.push("CLOSE_OF_ESCROW.desc");
        return query;
      }
      if (!query.order) {
        query.order = "CLOSE_OF_ESCROW.desc";
      }
    }
    return query;
  }

  static computeStatsFromItems(items) {
    const count = items.length;
    if (count === 0) return { count: 0 };
    const sum = (field) => items.reduce((a, r) => a + (Number(r[field]) || 0), 0);
    const avg = (field) => {
      const valid = items.filter(r => r[field] != null && r[field] !== '');
      return valid.length ? valid.reduce((a, r) => a + Number(r[field]), 0) / valid.length : null;
    };
    const avgNonZero = (field) => {
      const valid = items.filter(r => r[field] != null && r[field] !== '' && Number(r[field]) !== 0);
      return valid.length ? valid.reduce((a, r) => a + Number(r[field]), 0) / valid.length : null;
    };
    return {
      count,
      TOTAL_SOLD_PRICE: sum('SOLD_PRICE'),
      TOTAL_LIST_PRICE: sum('LIST_PRICE'),
      TOTAL_NEGOTIATION_PRICE: sum('NEGOTIATION_PRICE'),
      TOTAL_SQFT: sum('SQFT'),
      SOLD_PRICE: avg('SOLD_PRICE'),
      LIST_PRICE: avgNonZero('LIST_PRICE'),
      NEGOTIATION_PRICE: avg('NEGOTIATION_PRICE'),
      NEGOTIATION_CAP_RATE: avg('NEGOTIATION_CAP_RATE'),
      NEGOTIATION_PRICE_PER_SQFT: avg('NEGOTIATION_PRICE_PER_SQFT'),
      CAPITALIZATION_RATE: avgNonZero('CAPITALIZATION_RATE'),
      CLOSE_CAP_RATE: avgNonZero('CLOSE_CAP_RATE'),
      NET_OPERATING_INCOME: avgNonZero('NET_OPERATING_INCOME'),
      CLOSE_PRICE_PER_SQFT: avgNonZero('CLOSE_PRICE_PER_SQFT'),
      PRICE_PER_SQFT: avgNonZero('PRICE_PER_SQFT'),
      RENT_PER_SQFT: avgNonZero('RENT_PER_SQFT'),
      SQFT: avg('SQFT'),
      VACANCY: avg('VACANCY'),
    };
  }

  fetchNewDevelopmentIds() {
    getData({
      tableCode: NEW_DEVELOPMENT,
      options: {
        limit: {count: 1000, offset: 0},
        filterType: 'and',
        filter: [
          { code: 'DELETED', text: '', type: 'null' }
        ],
      },
    }).then(res => {
      if (res && res.success && res.data) {
        const ids = new Set(res.data.map(item => String(item.PROPERTY_ID)));
        this.setState({ newDevelopmentPropertyIds: ids });
      }
    });
  }

  fetchHotListIds() {
    const userEmail = this.props.userData && this.props.userData.email;
    if (!userEmail) {
      this.setState({ hotListPropertyIds: new Set() });
      return;
    }
    getData({
      tableCode: HOT_LIST,
      columns: ['ARCIMEDES_ROW_ID', 'EMAIL_ID', 'HOT_LIST_PROPERTIES'],
      options: {
        limit: {count: 1000, offset: 0},
        filterType: 'and',
        filter: [
          { code: 'EMAIL_ID', text: userEmail, type: 'exact' }
        ],
      },
    }).then(res => {
      if (res && res.success && res.data) {
        const ids = new Set();
        res.data.forEach(row => {
          const csv = row.HOT_LIST_PROPERTIES || '';
          csv.split(',').map(s => s.trim()).filter(Boolean).forEach(id => ids.add(String(id)));
        });
        this.setState({ hotListPropertyIds: ids });
      }
    });
  }

  fetchInvestorHotListCount() {
    const userEmail = this.props.userData && this.props.userData.email;
    if (!userEmail) return;
    executeQuery({
      queryName: INVESTOR_HOT_LIST_COUNT,
      options: { param: { USER_EMAIL: userEmail } },
      expireTime: 0,
    }).then((res) => {
      const count = res && res.success && Array.isArray(res.data) && res.data[0]
        ? Number(res.data[0].TOTAL_COUNT) || 0
        : 0;
      this.setState({ investorHotListCount: count });
    }).catch(() => {
      // non-fatal; count stays at 0
    });
  }

  fetchNeedOwnerIds() {
    getData({
      tableCode: NEED_OWNER,
      options: {
        limit: {count: 1000, offset: 0},
        filterType: 'and',
        filter: [
          { code: 'DELETED', text: '', type: 'null' }
        ],
      },
    }).then(res => {
      if (res && res.success && res.data) {
        const ids = new Set(res.data.map(item => String(item.PROPERTY_ID)));
        this.setState({ needOwnerPropertyIds: ids });
      }
    });
  }

  fetchAgentName(agentId) {
    if (!agentId) {
      this.setState({ agentName: null });
      return;
    }
    getData({
      tableCode: TEAM,
      options: {
        limit: { count: 1, offset: 0 },
        column: ['ARCIMEDES_ROW_ID', 'FIRST_NAME', 'LAST_NAME'],
        filter: [{ code: 'ARCIMEDES_ROW_ID', text: String(agentId), type: 'equal' }],
        filterType: 'and',
      },
    }).then(res => {
      if (res && res.success && res.data && res.data.length) {
        const agent = res.data[0];
        const name = [agent.FIRST_NAME, agent.LAST_NAME].filter(Boolean).join(' ');
        this.setState({ agentName: name });
      } else {
        this.setState({ agentName: null });
      }
    }).catch(() => {
      this.setState({ agentName: null });
    });
  }

  fetchTerritoryName(territoryId) {
    if (!territoryId) {
      this.setState({ territoryName: null });
      return;
    }
    const ids = String(territoryId).split(',').filter(Boolean);
    getData({
      tableCode: TERRITORIES_V2,
      options: {
        limit: { count: ids.length, offset: 0 },
        column: ['ARCIMEDES_ROW_ID', 'NAME'],
        filter: ids.map(id => ({ code: 'ARCIMEDES_ROW_ID', text: id, type: 'equal' })),
        filterType: 'or',
      },
    }).then(res => {
      if (res && res.success && res.data && res.data.length) {
        const names = res.data.map(t => t.NAME).filter(Boolean).join(', ');
        this.setState({ territoryName: names });
      } else {
        this.setState({ territoryName: null });
      }
    }).catch(() => {
      this.setState({ territoryName: null });
    });
  }

  fetchProductTypeName(productTypeId) {
    if (!productTypeId) {
      this.setState({ productTypeName: null });
      return;
    }
    const ids = String(productTypeId).split(',').filter(Boolean);
    executeBackendQuery({
      queryName: GET_ALL_PRODUCT_TYPES,
      options: { param: {} },
    }).then(res => {
      if (res && res.success && res.data && res.data.length) {
        const names = res.data
          .filter(pt => ids.includes(String(pt.ARCIMEDES_ROW_ID)))
          .map(pt => pt.NAME)
          .filter(Boolean)
          .join(', ');
        this.setState({ productTypeName: names || null });
      } else {
        this.setState({ productTypeName: null });
      }
    }).catch(() => {
      this.setState({ productTypeName: null });
    });
  }

  fetchMarketName(marketId) {
    if (!marketId) {
      this.setState({ marketName: null });
      return;
    }
    const ids = String(marketId).split(',').filter(Boolean);
    getData({
      tableCode: MARKETS,
      options: {
        limit: { count: ids.length, offset: 0 },
        column: ['ARCIMEDES_ROW_ID', 'NAME'],
        filter: ids.map(id => ({ code: 'ARCIMEDES_ROW_ID', text: id, type: 'equal' })),
        filterType: 'or',
      },
    }).then(res => {
      if (res && res.success && res.data && res.data.length) {
        const names = res.data.map(m => m.NAME).filter(Boolean).join(', ');
        this.setState({ marketName: names || null });
      } else {
        this.setState({ marketName: null });
      }
    }).catch(() => {
      this.setState({ marketName: null });
    });
  }

  initAgentFilter(query) {
    if (query.TERRITORY_LISTING_AGENT_TEAM) {
      const initial = { ...query };
      delete initial.page;
      delete initial.NEW_DEVELOPMENT;
      delete initial.HOT_LIST;
      delete initial.HOT_LIST_USER_EMAIL;
      delete initial.NEED_OWNER;
      this.setState({ initialAgentQuery: initial });
      this.fetchAgentName(query.TERRITORY_LISTING_AGENT_TEAM);
      this.fetchTerritoryName(query.TERRITORY);
      this.fetchProductTypeName(query.PRODUCT_TYPE);
      this.fetchMarketName(query.TERRITORY_MARKET);
    } else {
      this.setState({ agentName: null, territoryName: null, productTypeName: null, marketName: null, initialAgentQuery: null });
    }
    this.fetchOwnerResearchReviews(query);
  }

  // How many properties the owner research bot left for a person in the
  // territory shown, for the stats strip; the strip's link opens that list.
  // Ids, not the list's ZIPs: an agent's ZIPs outgrow a request line.
  fetchOwnerResearchReviews(query) {
    const filter = {};
    const idList = (value) => (value && /^\d+(,\d+)*$/.test(String(value)) ? String(value) : null);

    if (idList(query.TERRITORY_LISTING_AGENT_TEAM)) filter.agent = idList(query.TERRITORY_LISTING_AGENT_TEAM);
    if (idList(query.TERRITORY)) filter.territory = idList(query.TERRITORY);
    if (idList(query.TERRITORY_MARKET)) filter.territory_market = idList(query.TERRITORY_MARKET);

    const signature = JSON.stringify(filter);
    if (signature === this._ownerResearchSignature) return;
    this._ownerResearchSignature = signature;
    // Another territory: its count is not known until it answers, so the previous one's comes off the tile now.
    this.setState({ ownerResearchReviews: null });

    if (!Object.keys(filter).length || !can('true_owners')) return;

    const link = '/owner-research?' + Object.keys(filter).map((key) => key + '=' + encodeURIComponent(filter[key])).join('&');

    // Only the latest territory's answer lands: a slower earlier one would put its count and link on the tile.
    ownerResearchApi.reviews(1, filter).then((envelope) => {
      if (this._ownerResearchSignature !== signature) return;
      this.setState({ ownerResearchReviews: { count: envelope.meta.pagination.total, link } });
    }).catch(() => {
      if (this._ownerResearchSignature !== signature) return;
      this.setState({ ownerResearchReviews: null });
    });
  }

  fetch(q) {
    // Responses land in whatever order the server finishes them; only the
    // latest request may touch the screen.
    this._fetchSequence = (this._fetchSequence || 0) + 1;
    const fetchSequence = this._fetchSequence;

    const query = preprocessQueryFilters(q, this.props.userData);

    // A different filter is a different list: read the surviving selection here
    // and hand it to the rows this fetch brings back.
    const selectionSignature = Listing.selectionSignature(q || {});
    const keptSelection = this._selectionSignature === selectionSignature
      ? new Map(this.state.selectedById)
      : new Map();
    this._selectionSignature = selectionSignature;

    let page = "properties";
    let statsQueryName =
      query.hasOwnProperty("SOURCE") &&
      query.SOURCE === "SUMMIT" &&
      query.hasOwnProperty("STATUS") &&
      query.STATUS === "Active"
        ? V7_PROPERTIES_STATS_ACTIVE
        : V7_PROPERTIES_STATS;
    let propertiesQueryName = V7_PROPERTIES_LIST;
    let countQueryName = V7_PROPERTIES_STATS_COUNT;
    const isTeamFilter1 = query.hasOwnProperty("TERRITORY_LISTING_AGENT_TEAM") &&
      !["Territory1", "Territory2", "Territory3", "Territory4"].includes(
        query.TERRITORY_LISTING_AGENT_TEAM
      );
    const hasExtendedTerritoryFilter1 =
      query.hasOwnProperty("PRODUCT_TYPE") ||
      query.hasOwnProperty("TERRITORY") ||
      query.hasOwnProperty("TERRITORY_MARKET");

    if (isTeamFilter1 && !hasExtendedTerritoryFilter1) {
      page = "territories";
      statsQueryName = V9_PROPERTIES_STATS;
      countQueryName = V9_PROPERTIES_STATS_COUNT;
      propertiesQueryName = V9_PROPERTIES_LIST;
    } else if (isTeamFilter1 || hasExtendedTerritoryFilter1) {
      page = "territories";
      statsQueryName = V10_PROPERTIES_STATS;
      countQueryName = V10_PROPERTIES_STATS_COUNT;
      propertiesQueryName = V10_PROPERTIES_LIST;
    }
    const options = Listing.getOptions(query, page);
    // Stats/count queries return a single aggregate row — pagination offset must stay 0.
    const aggregateOptions = cloneDeep(options);
    aggregateOptions.param.offset = 0;
    aggregateOptions.param.limit = 1;

    const _options = cloneDeep(options);
    const _aggregateOptions = cloneDeep(aggregateOptions);
    let flagStatsQueryName = V7_TERRITORY_FLAG_STATS;
    let hotListCountQueryName = V9_HOT_LIST_COUNT;
    if (propertiesQueryName === V10_PROPERTIES_LIST) {
      flagStatsQueryName = V10_TERRITORY_FLAG_STATS;
      hotListCountQueryName = V10_HOT_LIST_COUNT;
    } else if (propertiesQueryName === V9_PROPERTIES_LIST) {
      flagStatsQueryName = V9_TERRITORY_FLAG_STATS;
      hotListCountQueryName = V9_HOT_LIST_COUNT;
    }
    // Build a separate query for "Closed – Last 12 Mo" count.
    // Reuses the same base filters but forces STATUS=Closed + rolling 12-month CLOSE_OF_ESCROW_MIN.
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
    const closed12mDateStr = `${(twelveMonthsAgo.getMonth() + 1).toString().padStart(2, '0')}/${twelveMonthsAgo.getDate().toString().padStart(2, '0')}/${twelveMonthsAgo.getFullYear()}`;
    const closed12mQuery = { ...query, STATUS: 'Closed', CLOSE_OF_ESCROW_MIN: closed12mDateStr };
    delete closed12mQuery.order;
    delete closed12mQuery.page;
    const closed12mOptions = filter(closed12mQuery, page);
    closed12mOptions.param.limit = 1;
    closed12mOptions.param.offset = 0;
    const _closed12mOptions = cloneDeep(closed12mOptions);

    const delayExec = (fn, ms) => new Promise(resolve => setTimeout(() => resolve(fn()), ms));

    // Every figure in the summary header comes from the five aggregate queries
    // below, and all five depend on the filters alone — sorting or paging cannot
    // change a single one of them. Re-running them on every column-header click
    // is what made a sorted reload feel like it hung, since the list itself is
    // the fastest of the six. Reuse the previous results whenever the filters are
    // untouched, so a sort re-queries the list only and the header stays exactly
    // as it was. (Claritask 548288 follow-up)
    const aggregateSignature = Listing.aggregateSignature(query, page, statsQueryName, countQueryName, flagStatsQueryName, hotListCountQueryName);
    const cached = this._aggregateCache && this._aggregateCache.signature === aggregateSignature
      ? this._aggregateCache.values
      : null;
    // Hand the cached rows back in the raw query-result shape so everything
    // downstream stays identical, and clone so the cache is never mutated by the
    // in-place stats adjustments below.
    const reuse = (index) => Promise.resolve(cloneDeep(cached[index]));

    // A different filter set is a different list: until its own figures land,
    // the previous list's numbers and rows must not stand in for them. Cached
    // figures land with the rows, so the strip blanks the same way until then.
    const loadingNewList = this._shownAggregateSignature !== undefined
      && this._shownAggregateSignature !== aggregateSignature;
    this.setState(loadingNewList
      ? { isBusy: true, loadingNewList: true, items: [], originalItems: [] }
      : { isBusy: true });

    Promise.all([
      delayExec(() => executeQuery({
        queryName: propertiesQueryName,
        options,
        _options,
        expireTime: 1000,
      }), 0),
      cached ? reuse(0) : delayExec(() => executeQuery({
        queryName: statsQueryName,
        options: aggregateOptions,
        _options: _aggregateOptions,
        expireTime: 1000,
      }), 15),
      cached ? reuse(1) : delayExec(() => executeQuery({
        queryName: countQueryName,
        options: aggregateOptions,
        _options: _aggregateOptions,
        expireTime: 1000,
      }), 30),
      cached ? reuse(2) : delayExec(() => flagStatsQueryName
        ? executeQuery({
            queryName: flagStatsQueryName,
            options: aggregateOptions,
            _options: _aggregateOptions,
            expireTime: 1000,
          }).catch(() => ({ success: false, data: [] }))
        : Promise.resolve({ success: false, data: [] }), 45),
      cached ? reuse(3) : delayExec(() => executeQuery({
        queryName: statsQueryName,
        options: closed12mOptions,
        _options: _closed12mOptions,
        expireTime: 1000,
      }).catch(() => ({ success: false, data: [] })), 60),
      cached ? reuse(4) : delayExec(() => hotListCountQueryName
        ? (() => {
            const userEmail = this.props.userData && this.props.userData.email;
            // Hot List is per-agent, not per-territory: count only by user
            // email so the figure ignores the current search filters.
            return executeQuery({
              queryName: hotListCountQueryName,
              options: { param: { HOT_LIST_USER_EMAIL: userEmail || '' } },
              expireTime: 1000,
            });
          })().catch(() => ({ success: false, data: [] }))
        : Promise.resolve({ success: false, data: [] }), 75),
    ])
      .then((values) => {
        if (fetchSequence !== this._fetchSequence) return;
        this._shownAggregateSignature = aggregateSignature;
        // Only cache a set the header can actually be drawn from; caching a
        // failure would freeze a blank header in place until the filters change.
        if (!cached && values[1] && values[1].success && values[2] && values[2].success) {
          this._aggregateCache = {
            signature: aggregateSignature,
            values: cloneDeep([values[1], values[2], values[3], values[4], values[5]]),
          };
        }
        let originalItems = values[0].success ? values[0].data : [];
        originalItems.forEach(
          (item) => (item.SELECTED = keptSelection.has(String(item.ARCIMEDES_ROW_ID)))
        );
        // Safety filter: some imported CLOSE_OF_ESCROW values are stored in US
        // format with 2-digit years (e.g. "6/16/25", "9/20/24 16:51") instead of
        // ISO. The server-side SQL filter does a plain string comparison, so
        // "6/16/25" >= "2026-01-01" is TRUE ("6" > "2") and such rows leak
        // through. Normalise every stored format to an ISO date before comparing.
        if (query.CLOSE_OF_ESCROW_MIN || query.CLOSE_OF_ESCROW_MAX) {
          const minISO = query.CLOSE_OF_ESCROW_MIN ? dateUStoISO(query.CLOSE_OF_ESCROW_MIN) : null;
          const maxISO = query.CLOSE_OF_ESCROW_MAX ? dateUStoISO(query.CLOSE_OF_ESCROW_MAX) : null;
          if (minISO || maxISO) {
            originalItems = originalItems.filter(item => {
              const coeISO = toISODateOnly(item.CLOSE_OF_ESCROW);
              if (!coeISO) return false;
              if (minISO && coeISO < minISO) return false;
              if (maxISO && coeISO > maxISO) return false;
              return true;
            });
          }
        }
        let order = "";
        let orderColumn = "";
        const allOrderArr = query.hasOwnProperty("order")
          ? (Array.isArray(query.order)
              ? query.order
              : String(query.order).split(',').map((s) => s.trim()).filter(Boolean))
          : [];
        if (allOrderArr.length > 1) {
          order = 0;
        } else if (allOrderArr.length === 1) {
          order = allOrderArr[0].split(".");
          orderColumn = order[0];
        }

        const isInNegotiationUnderContractView =
          query.hasOwnProperty("SOURCE") &&
          query.SOURCE === "SUMMIT" &&
          query.hasOwnProperty("STATUS") &&
          (query.STATUS === "In Negotiation,Under Contract" ||
            query.STATUS === "Under Contract,In Negotiation");

        if (isInNegotiationUnderContractView && allOrderArr.length > 1) {
          const primary = allOrderArr[0] ? allOrderArr[0].split(".") : [];
          const primaryCode = primary[0];
          const primaryDir = primary[1] || "asc";
          const secondarySpecs = allOrderArr.slice(1).map((s) => {
            const [code, dir] = s.split(".");
            return { code, direction: dir || "asc" };
          });
          if (primaryCode === "STATUS" && secondarySpecs.length > 0) {
            const inNeg = originalItems.filter((item) => item.STATUS === "In Negotiation");
            const under = originalItems.filter((item) => item.STATUS === "Under Contract");
            const inNegSorted = sortBySecondaryOrder(inNeg, secondarySpecs);
            const underSorted = sortBySecondaryOrder(under, secondarySpecs);
            originalItems =
              primaryDir === "asc"
                ? [...inNegSorted, ...underSorted]
                : [...underSorted, ...inNegSorted];
          } else {
            // General multi-column sort for In Negotiation/Under Contract view
            const allSpecs = allOrderArr.map((s) => {
              const [code, dir] = s.split(".");
              return { code, direction: dir || "asc" };
            });
            originalItems = sortBySecondaryOrder(originalItems, allSpecs);
          }
        } else if (
          isInNegotiationUnderContractView
        ) {
          if (
            (orderColumn === "STATUS" && order[1] === "asc") ||
            order === ""
          ) {
            let inNgotiationItems = originalItems.filter(
              (item) => item.STATUS === "In Negotiation"
            );
            inNgotiationItems.sort(
              (a, b) =>
                new Date(b.LAST_MODIFIED_DATE) - new Date(a.LAST_MODIFIED_DATE)
            );
            let underContractItems = originalItems.filter(
              (item) => item.STATUS === "Under Contract"
            );
            underContractItems.sort(
              (a, b) => new Date(b.EFFECTIVE_DATE) - new Date(a.EFFECTIVE_DATE)
            );
            originalItems = [...inNgotiationItems, ...underContractItems];
          } else if (orderColumn === "STATUS" && order[1] === "desc") {
            let inNgotiationItems = originalItems.filter(
              (item) => item.STATUS === "In Negotiation"
            );
            inNgotiationItems.sort(
              (a, b) =>
                new Date(a.LAST_MODIFIED_DATE) - new Date(b.LAST_MODIFIED_DATE)
            );
            let underContractItems = originalItems.filter(
              (item) => item.STATUS === "Under Contract"
            );
            underContractItems.sort(
              (a, b) => new Date(a.EFFECTIVE_DATE) - new Date(b.EFFECTIVE_DATE)
            );
            originalItems = [...underContractItems, ...inNgotiationItems];
          }
        } else if (allOrderArr.length > 1) {
          // Multi-column sort: apply client-side sorting that respects the
          // user's click order (primary sort first, then secondary, etc.)
          // because Arcimedes ORDER BY uses fixed column positions and ignores
          // the order in which CO parameters are passed.
          const allSpecs = allOrderArr
            .filter((s) => s.split(".")[0] !== "ARCIMEDES_ROW_ID")
            .map((s) => {
              const [code, dir] = s.split(".");
              return { code, direction: dir || "asc" };
            });
          if (allSpecs.length > 0) {
            originalItems = sortBySecondaryOrder(originalItems, allSpecs, page === "territories");
          }
        } else if (orderColumn === "AGENT_1_LAST_NAME" || order === "") {
          const dir =
            orderColumn === "AGENT_1_LAST_NAME" ? order[1] || "asc" : "asc";
          originalItems = [...originalItems].sort((a, b) =>
            compareAgentName(a, b, dir)
          );
        } else if (orderColumn === "CURRENT_OWNER_NAME") {
          const dir = order[1] || "asc";
          originalItems = [...originalItems].sort((a, b) =>
            compareOwnerName(a, b, dir)
          );
        } else if (DATE_COLUMNS.has(orderColumn)) {
          const dir = order[1] || "asc";
          originalItems = [...originalItems].sort((a, b) => {
            const cmp = compareDates(a[orderColumn], b[orderColumn]);
            return dir === "desc" ? -cmp : cmp;
          });
        }

        const existingItems = cloneDeep(this.state.items);
        let items = cloneDeep(originalItems);
        // Create a Set to store unique names
        const uniqueNames = new Set();
        // Iterate through the array of objects and add names to the Set
        items.forEach((obj) => {
          if (obj.LENDER_NAME) {
            uniqueNames.add(obj.LENDER_NAME);
          }
        });

        // Get the count of unique names
        const uniqueNamesCount = uniqueNames.size;
        let selectedNo = 0;
        let unselectedNo = originalItems.length ? originalItems.length : 0;
        let areRemovedItems = this.state.areRemovedItems;

        // Merge response items with selected ones
        if (
          values[0].success &&
          areRemovedItems &&
          query.page === this.state.query.page
        ) {
          let newItems = [];
          for (let index = 0; index < originalItems.length; index++) {
            const itemFound = existingItems.find(
              (item) =>
                originalItems[index].ARCIMEDES_ROW_ID === item.ARCIMEDES_ROW_ID
            );
            if (itemFound) {
              newItems.push(itemFound);
            }
          }

          unselectedNo = 0;
          newItems.forEach((newItem) => {
            if (newItem.SELECTED) {
              selectedNo++;
            } else {
              unselectedNo++;
            }
          });
          items = newItems;
        }

        // Returning from the map view with a narrowing active (see
        // componentDidMount). Intersect by row id rather than restoring the
        // stored rows wholesale, so `originalItems` stays the full fetched page
        // and "Reset List" can still bring everything back.
        if (this._restoreNarrowed) {
          const restored = this._restoreNarrowed;
          this._restoreNarrowed = null;

          const selectedById = new Map(
            restored.map((item) => [String(item.ARCIMEDES_ROW_ID), !!item.SELECTED])
          );
          const narrowed = items.filter((item) =>
            selectedById.has(String(item.ARCIMEDES_ROW_ID))
          );

          if (narrowed.length && narrowed.length < items.length) {
            narrowed.forEach((item) => {
              item.SELECTED = selectedById.get(String(item.ARCIMEDES_ROW_ID));
            });
            items = narrowed;
            areRemovedItems = true;
            selectedNo = narrowed.filter((item) => item.SELECTED).length;
            unselectedNo = narrowed.length - selectedNo;
          }
        }

        // Safely extract count - handle failed queries where data is false instead of array
        let totalCount =
          values[2].success &&
          values[2].data &&
          Array.isArray(values[2].data) &&
          values[2].data.length
            ? typeof values[2].data[0].count === "string"
              ? Number(values[2].data[0].count)
              : values[2].data[0].count
            : limit;

        // Safely extract stats - handle failed queries
        let stats =
          values[1].success &&
          values[1].data &&
          Array.isArray(values[1].data) &&
          values[1].data.length
            ? values[1].data[0]
            : null;

        // Attach rolling 12-month closed stats from the separate query (values[4])
        if (stats) {
          const closed12mRow =
            values[4] && values[4].success && Array.isArray(values[4].data) && values[4].data.length
              ? values[4].data[0]
              : null;
          if (closed12mRow) {
            stats.CLOSED_LAST_12M = closed12mRow.count;
            stats.CLOSED_CAP_LAST_12M = closed12mRow.CLOSE_CAP_RATE;
          }

          // Override averages with non-zero versions (excludes 0 from average)
          const nzFields = ['LIST_PRICE', 'CAPITALIZATION_RATE', 'CLOSE_CAP_RATE', 'NET_OPERATING_INCOME', 'PRICE_PER_SQFT', 'CLOSE_PRICE_PER_SQFT', 'RENT_PER_SQFT', 'SOLD_PRICE', 'VACANCY'];
          nzFields.forEach(f => {
            const s = Number(stats[f + '_NZ_SUM']) || 0;
            const c = Number(stats[f + '_NZ_CNT']) || 0;
            if (c > 0) stats[f] = s / c;
          });
        }

        const flagStatsRow =
          values[3] && values[3].success && Array.isArray(values[3].data) && values[3].data.length
            ? values[3].data[0]
            : null;
        const hotListRow =
          values[5] && values[5].success && Array.isArray(values[5].data) && values[5].data.length
            ? values[5].data[0]
            : null;
        const territoryFlagCounts = flagStatsRow
          ? {
              hotList: hotListRow ? Number(hotListRow.HOT_LIST_COUNT) || 0 : 0,
              newDev: Number(flagStatsRow.NEW_DEV_COUNT) || 0,
              needOwner: (Number(flagStatsRow.TOTAL_PROPERTY_COUNT) || 0)
                - (Number(flagStatsRow.HAS_OWNERSHIP_COUNT) || 0)
                + (Number(flagStatsRow.NEED_OWNER_WITH_OWNERSHIP_COUNT) || 0),
              researched: Number(flagStatsRow.RESEARCHED_COUNT) || 0,
              needInfo: Number(flagStatsRow.NEED_INFO_COUNT) || 0,
            }
          : null;

        // Rows can arrive ticked from somewhere other than the running
        // selection — the map view hands back the rows it was narrowed to,
        // ticks and all — so fold the page's ticked rows in before counting.
        items.forEach((item) => {
          if (item.SELECTED) {
            keptSelection.set(String(item.ARCIMEDES_ROW_ID), item);
          }
        });

        // Page-scoped counts, read off the rows rather than tracked alongside
        // them: Remove Selected / Remove Unselected act on what is on screen.
        selectedNo = items.filter((item) => item.SELECTED).length;
        unselectedNo = items.length - selectedNo;

        this.setState({
          selectedById: keptSelection,
          selectedAll: items.length > 0 && selectedNo === items.length,
          originalItems: originalItems,
          items: items,
          uniquePropCount: uniqueNamesCount,
          totalCount: totalCount,
          stats: stats,
          territoryFlagCounts,
          loadingNewList: false,
          error: values[0].success ? null : values[0].message,
          isBusy: null,
          query,
          underwritingSuccess: false,
          underwritingError: false,
          selectedNo: selectedNo,
          unselectedNo: unselectedNo,
          areRemovedItems: areRemovedItems,
        });

      })
      .catch((err) => {
        if (fetchSequence !== this._fetchSequence) return;
        console.error("Error fetching properties:", err);
        // A list that never landed has no figures: the previous list's must not come back under its URL.
        if (loadingNewList) this._shownAggregateSignature = undefined;
        this.setState({
          isBusy: null,
          loadingNewList: false,
          error: err.message || "Unknown error",
          ...(loadingNewList ? { stats: null, territoryFlagCounts: null } : {}),
        });
      });
  }

  componentDidUpdate(prevProps, prevState) {
    if (!shallowequal(prevProps.location.query, this.props.location.query)) {
      // New filter/sort navigation: keep at top unless this navigation is a
      // property->list restore (handled once in componentDidMount).
      this._shouldRestoreListScroll = false;
      this._pinnedInitialScroll = false;
      window.scrollTo(0, 0);
      this.fetch(normalizeListQuery(this.props.location.query));
      this.fetchOwnerResearchReviews(this.props.location.query);

      const prevAgentId = prevProps.location.query.TERRITORY_LISTING_AGENT_TEAM;
      const currAgentId = this.props.location.query.TERRITORY_LISTING_AGENT_TEAM;
      if (currAgentId !== prevAgentId) {
        this.fetchAgentName(currAgentId);
        if (currAgentId) {
          const initial = { ...this.props.location.query };
          delete initial.page;
          delete initial.NEW_DEVELOPMENT;
          delete initial.HOT_LIST;
          delete initial.HOT_LIST_USER_EMAIL;
          delete initial.NEED_OWNER;
          this.setState({ initialAgentQuery: initial });
        } else {
          this.setState({ initialAgentQuery: null, territoryName: null });
        }
      }
      const prevTerritory = prevProps.location.query.TERRITORY;
      const currTerritory = this.props.location.query.TERRITORY;
      if (currTerritory !== prevTerritory) {
        this.fetchTerritoryName(currTerritory);
      }
      const prevProductType = prevProps.location.query.PRODUCT_TYPE;
      const currProductType = this.props.location.query.PRODUCT_TYPE;
      if (currProductType !== prevProductType) {
        this.fetchProductTypeName(currProductType);
      }
      const prevMarket = prevProps.location.query.TERRITORY_MARKET;
      const currMarket = this.props.location.query.TERRITORY_MARKET;
      if (currMarket !== prevMarket) {
        this.fetchMarketName(currMarket);
      }
    }

    const hadItems = prevState.items && prevState.items.length;
    const hasItems = this.state.items && this.state.items.length;
    if (
      !this._shouldRestoreListScroll &&
      !this._pinnedInitialScroll &&
      !hadItems &&
      hasItems
    ) {
      this._pinnedInitialScroll = true;
      const pinTop = () => {
        if (!this._shouldRestoreListScroll) {
          window.scrollTo(0, 0);
        }
      };
      pinTop();
      // Owner cells / stats can still shift layout after first paint.
      requestAnimationFrame(pinTop);
      [0, 250, 1000, 2000].forEach((ms) => setTimeout(pinTop, ms));
    }

    if (this.state.comps === "sales" && this.state.saleCompItems && !this._salesPrintPending) {
      this._salesPrintPending = true;
      waitForPrintImages().then(() => {
        this._salesPrintPending = false;
        printLandscape();
        this.setState({ comps: "", saleCompItems: null });
      });
    }
  }
  componentWillUnmount() {
    this.setState({ comps: "" });
    window.removeEventListener("resize", this.updateHeaderOffset);
    if (this._headerResizeObserver) {
      this._headerResizeObserver.disconnect();
      this._headerResizeObserver = null;
    }
    if (
      typeof history !== "undefined" &&
      "scrollRestoration" in history &&
      this._prevScrollRestoration != null
    ) {
      history.scrollRestoration = this._prevScrollRestoration;
    }
  }

  afterPrint() {
    this.setState({ comps: "", saleCompItems: null });
  }

  handleCheckboxChange(e) {
    const items = cloneDeep(this.state.items);
    const name = e.target.name;
    if (name === "selectAll") {
      const select = !e.target.checked;
      items.forEach((item) => {
        item.SELECTED = select;
      });
    } else {
      items[name].SELECTED = !items[name].SELECTED;
    }

    // Fold the page's rows back into the running selection: rows on the other
    // pages are not on screen and are left exactly as they were.
    const selectedById = new Map(this.state.selectedById);
    items.forEach((item) => {
      const id = String(item.ARCIMEDES_ROW_ID);
      if (item.SELECTED) {
        selectedById.set(id, item);
      } else {
        selectedById.delete(id);
      }
    });

    const selectedNo = items.filter((item) => item.SELECTED).length;

    this.setState({
      items,
      selectedById,
      selectedNo,
      unselectedNo: items.length - selectedNo,
      // Read off the rows, so ticking the last one by hand leaves the header
      // box in the state it describes.
      selectedAll: items.length > 0 && selectedNo === items.length,
    });
  }

  /*  removeSelected() {
         const items = this.state.items.filter(item => !item.SELECTED);
         this.setState({
            items,
             selectedNo: 0,
             areRemovedItems: true,
         });
     } */

  removeSelected() {
    const items = this.state.items.filter((item) => !item.SELECTED);
    const removedItems = this.state.items.filter((item) => item.SELECTED);
    let stats = {
      count: 0,
      LIST_PRICE: 0,
      NEGOTIATION_PRICE: 0,
      SOLD_PRICE: 0,
      HSC_PRICE: 0,
      SQFT: 0,
      VACANCY: 0,
      DOWN_PAYMENT: 0,
      SELLER_FEE_PERCENTAGE: 0,
      NET_OPERATING_INCOME: 0,
      CLOSE_NOI: 0,
      CAPITALIZATION_RATE: 0,
      NEGOTIATION_CAP_RATE: 0,
      CLOSE_CAP_RATE: 0,
      RENT_PER_SQFT: 0,
      PRICE_PER_SQFT: 0,
      NEGOTIATION_PRICE_PER_SQFT: 0,
      CLOSE_PRICE_PER_SQFT: 0,
      LOAN_AMOUNT: 0,
      UNITS: 0,
      LTV: 0,
      INTEREST_RATE: 0,
    };
    const sums = {
      LIST_PRICE: 0,
      NEGOTIATION_PRICE: 0,
      SOLD_PRICE: 0,
      HSC_PRICE: 0,
      SQFT: 0,
      VACANCY: 0,
      DOWN_PAYMENT: 0,
      SELLER_FEE_PERCENTAGE: 0,
      NET_OPERATING_INCOME: 0,
      CLOSE_NOI: 0,
      CAPITALIZATION_RATE: 0,
      NEGOTIATION_CAP_RATE: 0,
      CLOSE_CAP_RATE: 0,
      RENT_PER_SQFT: 0,
      PRICE_PER_SQFT: 0,
      NEGOTIATION_PRICE_PER_SQFT: 0,
      CLOSE_PRICE_PER_SQFT: 0,
      LOAN_AMOUNT: 0,
      UNITS: 0,
      LTV: 0,
      INTEREST_RATE: 0,
    };

    removedItems.forEach((item) => {
      for (var i = 0; i < Object.getOwnPropertyNames(stats).length; i++) {
        var eachPropertyValue = item[Object.getOwnPropertyNames(stats)[i]];
        if (eachPropertyValue != null && eachPropertyValue != "") {
          stats[Object.getOwnPropertyNames(stats)[i]]++;
        }
      }
      Object.keys(sums).forEach((key) => {
        sums[key] += Number(item[key]);
      });
    });

    let resetStats = { ...stats };
    resetStats.TOTAL_LIST_PRICE =
      this.state.stats.TOTAL_LIST_PRICE - sums.LIST_PRICE;
    resetStats.TOTAL_NEGOTIATION_PRICE =
      this.state.stats.TOTAL_NEGOTIATION_PRICE - sums.NEGOTIATION_PRICE;
    resetStats.TOTAL_SOLD_PRICE =
      this.state.stats.TOTAL_SOLD_PRICE - sums.SOLD_PRICE;
    resetStats.TOTAL_HSC_PRICE =
      this.state.stats.TOTAL_HSC_PRICE - sums.HSC_PRICE;
    resetStats.TOTAL_SQFT = this.state.stats.TOTAL_SQFT - sums.SQFT;
    resetStats.TOTAL_LOAN_AMOUNT =
      this.state.stats.TOTAL_LOAN_AMOUNT - sums.LOAN_AMOUNT;
    resetStats.TOTAL_UNITS = this.state.stats.TOTAL_UNITS - sums.UNITS;

    resetStats.TOTAL_CAPITALIZATION_RATE =
      this.state.stats.TOTAL_CAPITALIZATION_RATE - sums.CAPITALIZATION_RATE;
    resetStats.TOTAL_VACANCY = this.state.stats.TOTAL_VACANCY - sums.VACANCY;
    resetStats.TOTAL_CLOSE_NOI =
      this.state.stats.TOTAL_CLOSE_NOI - sums.CLOSE_NOI;
    resetStats.TOTAL_NET_OPERATING_INCOME =
      this.state.stats.TOTAL_NET_OPERATING_INCOME - sums.NET_OPERATING_INCOME;
    resetStats.TOTAL_PRICE_PER_SQFT =
      this.state.stats.TOTAL_PRICE_PER_SQFT - sums.PRICE_PER_SQFT;
    resetStats.TOTAL_RENT_PER_SQFT =
      this.state.stats.TOTAL_RENT_PER_SQFT - sums.RENT_PER_SQFT;

    resetStats.TOTAL_NEGOTIATION_PRICE_PER_SQFT =
      this.state.stats.TOTAL_NEGOTIATION_PRICE_PER_SQFT -
      sums.NEGOTIATION_PRICE_PER_SQFT;
    resetStats.TOTAL_CLOSE_PRICE_PER_SQFT =
      this.state.stats.TOTAL_CLOSE_PRICE_PER_SQFT - sums.CLOSE_PRICE_PER_SQFT;
    resetStats.TOTAL_INTEREST_RATE =
      this.state.stats.TOTAL_INTEREST_RATE - sums.INTEREST_RATE;
    resetStats.TOTAL_CLOSE_CAP_RATE =
      this.state.stats.TOTAL_CLOSE_CAP_RATE - sums.CLOSE_CAP_RATE;
    resetStats.TOTAL_NEGOTIATION_CAP_RATE =
      this.state.stats.TOTAL_NEGOTIATION_CAP_RATE - sums.NEGOTIATION_CAP_RATE;
    resetStats.TOTAL_LTV = this.state.stats.TOTAL_LTV - sums.LTV;

    resetStats = {
      ...resetStats,
      LIST_PRICE:
        resetStats.TOTAL_LIST_PRICE / this.state.stats["LIST_PRICE_count"] -
        stats["LIST_PRICE"],
      NEGOTIATION_PRICE:
        resetStats.TOTAL_NEGOTIATION_PRICE /
          this.state.stats["NEGOTIATION_PRICE_count"] -
        stats["NEGOTIATION_PRICE"],
      SOLD_PRICE:
        resetStats.TOTAL_SOLD_PRICE / this.state.stats["SOLD_PRICE_count"] -
        stats["SOLD_PRICE"],
      HSC_PRICE:
        resetStats.TOTAL_HSC_PRICE / this.state.stats["HSC_PRICE_count"] -
        stats["HSC_PRICE"],
      CAPITALIZATION_RATE:
        resetStats.TOTAL_CAPITALIZATION_RATE /
          this.state.stats["CAPITALIZATION_RATE_count"] -
        stats["CAPITALIZATION_RATE"],
      CLOSE_NOI:
        resetStats.TOTAL_CLOSE_NOI / this.state.stats["CLOSE_NOI_count"] -
        stats["CLOSE_NOI"],
      NET_OPERATING_INCOME:
        resetStats.TOTAL_NET_OPERATING_INCOME /
          this.state.stats["NET_OPERATING_INCOME_count"] -
        stats["NET_OPERATING_INCOME"],
      PRICE_PER_SQFT:
        resetStats.TOTAL_PRICE_PER_SQFT /
          this.state.stats["PRICE_PER_SQFT_count"] -
        stats["PRICE_PER_SQFT"],
      RENT_PER_SQFT:
        resetStats.TOTAL_RENT_PER_SQFT /
          this.state.stats["RENT_PER_SQFT_count"] -
        stats["RENT_PER_SQFT"],
      SQFT:
        resetStats.TOTAL_SQFT / this.state.stats["SQFT_count"] - stats["SQFT"],
      VACANCY:
        resetStats.TOTAL_VACANCY / this.state.stats["VACANCY_count"] -
        stats["VACANCY"],

      NEGOTIATION_PRICE_PER_SQFT:
        resetStats.TOTAL_NEGOTIATION_PRICE_PER_SQFT /
          this.state.stats["NEGOTIATION_PRICE_PER_SQFT_count"] -
        stats["NEGOTIATION_PRICE_PER_SQFT"],
      CLOSE_PRICE_PER_SQFT:
        resetStats.TOTAL_CLOSE_PRICE_PER_SQFT /
          this.state.stats["CLOSE_PRICE_PER_SQFT_count"] -
        stats["CLOSE_PRICE_PER_SQFT"],
      INTEREST_RATE:
        resetStats.TOTAL_INTEREST_RATE /
          this.state.stats["INTEREST_RATE_count"] -
        stats["INTEREST_RATE"],
      CLOSE_CAP_RATE:
        resetStats.TOTAL_CLOSE_CAP_RATE /
          this.state.stats["CLOSE_CAP_RATE_count"] -
        stats["CLOSE_CAP_RATE"],
      NEGOTIATION_CAP_RATE:
        resetStats.TOTAL_NEGOTIATION_CAP_RATE /
          this.state.stats["NEGOTIATION_CAP_RATE_count"] -
        stats["NEGOTIATION_CAP_RATE"],
      LTV: resetStats.TOTAL_LTV / this.state.stats["LTV_count"] - stats["LTV"],
    };

    resetStats["LIST_PRICE_count"] =
      this.state.stats["LIST_PRICE_count"] - stats["LIST_PRICE"];
    resetStats["NEGOTIATION_PRICE_count"] =
      this.state.stats["NEGOTIATION_PRICE_count"] - stats["NEGOTIATION_PRICE"];
    resetStats["SOLD_PRICE_count"] =
      this.state.stats["SOLD_PRICE_count"] - stats["SOLD_PRICE"];
    resetStats["HSC_PRICE_count"] =
      this.state.stats["HSC_PRICE_count"] - stats["HSC_PRICE"];
    resetStats["CAPITALIZATION_RATE_count"] =
      this.state.stats["CAPITALIZATION_RATE_count"] -
      stats["CAPITALIZATION_RATE"];
    resetStats["CLOSE_NOI_count"] =
      this.state.stats["CLOSE_NOI_count"] - stats["CLOSE_NOI"];
    resetStats["NET_OPERATING_INCOME_count"] =
      this.state.stats["NET_OPERATING_INCOME_count"] -
      stats["NET_OPERATING_INCOME"];
    resetStats["PRICE_PER_SQFT_count"] =
      this.state.stats["PRICE_PER_SQFT_count"] - stats["PRICE_PER_SQFT"];
    resetStats["RENT_PER_SQFT_count"] =
      this.state.stats["RENT_PER_SQFT_count"] - stats["RENT_PER_SQFT"];
    resetStats["SQFT_count"] = this.state.stats["SQFT_count"] - stats["SQFT"];
    resetStats["VACANCY_count"] =
      this.state.stats["VACANCY_count"] - stats["VACANCY"];

    resetStats["NEGOTIATION_PRICE_PER_SQFT_count"] =
      this.state.stats["NEGOTIATION_PRICE_PER_SQFT_count"] -
      stats["NEGOTIATION_PRICE_PER_SQFT"];
    resetStats["CLOSE_PRICE_PER_SQFT_count"] =
      this.state.stats["CLOSE_PRICE_PER_SQFT_count"] -
      stats["CLOSE_PRICE_PER_SQFT"];
    resetStats["INTEREST_RATE_count"] =
      this.state.stats["INTEREST_RATE_count"] - stats["INTEREST_RATE"];
    resetStats["CLOSE_CAP_RATE_count"] =
      this.state.stats["CLOSE_CAP_RATE_count"] - stats["CLOSE_CAP_RATE"];
    resetStats["NEGOTIATION_CAP_RATE_count"] =
      this.state.stats["NEGOTIATION_CAP_RATE_count"] -
      stats["NEGOTIATION_CAP_RATE"];
    resetStats["LTV_count"] = this.state.stats["LTV_count"] - stats["LTV"];

    resetStats.count = Number(this.state.totalCount - removedItems.length);
    this.setState({
      stats: resetStats,
      selectionStats: true,
      totalCount: Number(this.state.totalCount - removedItems.length),
    });

    // The removed rows are off the list, so they come off the selection too.
    const selectedById = new Map(this.state.selectedById);
    removedItems.forEach((item) => selectedById.delete(String(item.ARCIMEDES_ROW_ID)));

    this.setState({
      items,
      selectedById,
      selectedNo: 0,
      areRemovedItems: true,
    });
  }
  deleteSelected() {
    this.setState({ showConfirm: true });
  }

  /**
   * The ticked rows — the set every bulk action works on.
   *
   * Read from the running selection rather than from the rows on screen, so a
   * bulk action covers everything ticked across the pages walked so far.
   */
  selectedItems() {
    return Array.from(this.state.selectedById.values());
  }

  /**
   * Tick every property the filter matches, not just the page on screen.
   *
   * The way past the page at a time limit: the list hands over its whole
   * filtered set (capped, as the mail merge is), and every row in it joins the
   * selection.
   */
  selectAllMatching() {
    this.setState({ selectingAllMatching: true });

    this.fetchFullFilteredSet()
      .then((allItems) => {
        const selectedById = new Map();
        (allItems || []).forEach((item) => {
          if (item && item.ARCIMEDES_ROW_ID) {
            selectedById.set(String(item.ARCIMEDES_ROW_ID), item);
          }
        });

        const items = cloneDeep(this.state.items);
        items.forEach((item) => {
          item.SELECTED = selectedById.has(String(item.ARCIMEDES_ROW_ID));
        });
        const selectedNo = items.filter((item) => item.SELECTED).length;

        this.setState({
          items,
          selectedById,
          selectedNo,
          unselectedNo: items.length - selectedNo,
          selectedAll: items.length > 0 && selectedNo === items.length,
          selectingAllMatching: false,
        });
      })
      .catch(() => this.setState({ selectingAllMatching: false }));
  }

  clearSelection() {
    const items = cloneDeep(this.state.items);
    items.forEach((item) => {
      item.SELECTED = false;
    });

    this.setState({
      items,
      selectedById: new Map(),
      selectedNo: 0,
      unselectedNo: items.length,
      selectedAll: false,
    });
  }

  openBulkEdit() {
    this.setState({ bulkEditOpen: true, bulkEditResult: null, error: null });
  }

  closeBulkEdit() {
    this.setState({ bulkEditOpen: false });
  }

  openFindDups() {
    this.setState({ findDupsOpen: true, error: null });
  }

  closeFindDups() {
    this.setState({ findDupsOpen: false });
  }

  openMerge() {
    const selected = this.selectedItems();
    if (selected.length < 2 || selected.length > MERGE_MAX_PROPERTIES) return;

    this.setState({
      mergeOpen: true,
      mergeResult: null,
      error: null,
      mergeProperties: selected.map((row) => ({
        id: Number(row.ARCIMEDES_ROW_ID),
        name: row.NAME,
        address: row.ADDRESS,
        city: row.CITY,
        state: row.STATE,
        zip: row.ZIP,
        apn: row.APN,
        source: row.SOURCE,
        status: row.STATUS,
      })),
    });
  }

  closeMerge() {
    this.setState({ mergeOpen: false });
  }

  onMerged(data) {
    this._aggregateCache = null;
    // Cleared on the rows too: with Remove Selected active, fetch() keeps the
    // on-screen rows and would fold their ticks back into the selection.
    const items = cloneDeep(this.state.items);
    items.forEach((item) => {
      item.SELECTED = false;
    });

    this.setState({
      mergeOpen: false,
      mergeResult: mergeSummary(data),
      items,
      selectedById: new Map(),
      selectedAll: false,
      selectedNo: 0,
      unselectedNo: items.length,
    }, () => {
      this.fetch(this.props.location.query);
    });
  }

  onBulkEditSaved(result) {
    // The rows changed while the filters stayed identical, so the reuse cache
    // would otherwise re-serve the pre-edit header — same reason as a delete.
    this._aggregateCache = null;
    this.setState({
      bulkEditOpen: false,
      bulkEditResult: result,
      selectedById: new Map(),
      selectedAll: false,
      selectedNo: 0,
    }, () => {
      this.fetch(this.props.location.query);
    });
  }

  removeUnselected() {
    const items = this.state.items.filter((item) => item.SELECTED);

    const removedUnselectedItems = this.state.items.filter(
      (item) => item.SELECTED
    );
    let stats = {
      count: 0,
      LIST_PRICE: 0,
      NEGOTIATION_PRICE: 0,
      SOLD_PRICE: 0,
      HSC_PRICE: 0,
      SQFT: 0,
      VACANCY: 0,
      DOWN_PAYMENT: 0,
      SELLER_FEE_PERCENTAGE: 0,
      NET_OPERATING_INCOME: 0,
      CLOSE_NOI: 0,
      CAPITALIZATION_RATE: 0,
      NEGOTIATION_CAP_RATE: 0,
      CLOSE_CAP_RATE: 0,
      RENT_PER_SQFT: 0,
      PRICE_PER_SQFT: 0,
      NEGOTIATION_PRICE_PER_SQFT: 0,
      CLOSE_PRICE_PER_SQFT: 0,
    };
    const sums = {
      LIST_PRICE: 0,
      NEGOTIATION_PRICE: 0,
      SOLD_PRICE: 0,
      HSC_PRICE: 0,
      SQFT: 0,
      VACANCY: 0,
      DOWN_PAYMENT: 0,
      SELLER_FEE_PERCENTAGE: 0,
      NET_OPERATING_INCOME: 0,
      CLOSE_NOI: 0,
      CAPITALIZATION_RATE: 0,
      NEGOTIATION_CAP_RATE: 0,
      CLOSE_CAP_RATE: 0,
      RENT_PER_SQFT: 0,
      PRICE_PER_SQFT: 0,
      NEGOTIATION_PRICE_PER_SQFT: 0,
      CLOSE_PRICE_PER_SQFT: 0,
    };

    items.forEach((item) => {
      if (
        item.STATUS !== "Active" ||
        (item.STATUS === "Active" && item.LIST_PRICE)
      ) {
        stats.count++;
        for (var i = 0; i < Object.getOwnPropertyNames(stats).length; i++) {
          var eachPropertyValue = item[Object.getOwnPropertyNames(stats)[i]];
          if (eachPropertyValue != null && eachPropertyValue != "") {
            stats[Object.getOwnPropertyNames(stats)[i]]++;
          }
        }
      }
      Object.keys(sums).forEach((key) => {
        sums[key] += Number(item[key]);
      });
    });

    stats.TOTAL_LIST_PRICE = sums.LIST_PRICE;
    stats.TOTAL_NEGOTIATION_PRICE = sums.NEGOTIATION_PRICE;
    stats.TOTAL_SOLD_PRICE = sums.SOLD_PRICE;
    stats.TOTAL_HSC_PRICE = sums.HSC_PRICE;
    stats.TOTAL_SQFT = sums.SQFT;

    if (stats.count) {
      Object.keys(sums).forEach((key) => {
        stats[key] = sums[key] / stats[key];
      });
    }

    this.setState({
      items,
      unselectedNo: 0,
      areRemovedItems: false,
      stats,
      selectionStats: true,
      totalCount: Number(this.state.totalCount - removedUnselectedItems.length),
    });
  }

  reset() {
    // The user explicitly wants the full list back — drop any pending hand-off so
    // it cannot re-narrow the list on the next list <-> map hop.
    clearNarrowedHandoff();
    this._restoreNarrowed = null;

    const originalItems = this.state.originalItems;

    if (this.state.selectionStats) {
      this.setState({ isBusy: true });

      const queryBase = { ...this.state.query };
      if (queryBase.HOT_LIST && this.props.userData && this.props.userData.email) {
        queryBase.HOT_LIST_USER_EMAIL = this.props.userData.email;
      }
      const query = Listing.setQueryOrder(queryBase);
      const options = Listing.getOptions(query);
      const aggregateOptions = cloneDeep(options);
      aggregateOptions.param.offset = 0;
      aggregateOptions.param.limit = 1;
      const statsQueryName =
        query.hasOwnProperty("SOURCE") &&
        query.SOURCE === "SUMMIT" &&
        query.hasOwnProperty("STATUS") &&
        query.STATUS === "Active"
          ? query.hasOwnProperty("TERRITORY_LISTING_AGENT_TEAM") &&
            query.TERRITORY_LISTING_AGENT_TEAM != null
            ? V9_PROPERTIES_STATS_ACTIVE
            : V7_PROPERTIES_STATS_ACTIVE
          : query.hasOwnProperty("TERRITORY_LISTING_AGENT_TEAM") &&
            query.TERRITORY_LISTING_AGENT_TEAM != null
          ? V9_PROPERTIES_STATS
          : V7_PROPERTIES_STATS;

      executeQuery({
        queryName: statsQueryName,
        options: aggregateOptions,
        expireTime: 1000 * 10,
      }).then((res) => {
        this.setState({
          isBusy: null,
          stats: res.data[0],
          items: cloneDeep(originalItems).map((item) => ({ ...item, SELECTED: false })),
          selectedById: new Map(),
          selectedAll: false,
          selectedNo: 0,
          unselectedNo: originalItems.length,
          areRemovedItems: false,
          selectionStats: false,
          totalCount: originalItems.length,
        });
      });
      return;
    }

    this.setState({
      items: cloneDeep(originalItems).map((item) => ({ ...item, SELECTED: false })),
      selectedById: new Map(),
      selectedAll: false,
      selectedNo: 0,
      unselectedNo: originalItems.length,
      areRemovedItems: false,
      selectionStats: false,
    });
  }

  updateStats() {
    const stats = {
      count: 0,
      LIST_PRICE: 0,
      NEGOTIATION_PRICE: 0,
      SOLD_PRICE: 0,
      HSC_PRICE: 0,
      SQFT: 0,
      VACANCY: 0,
      DOWN_PAYMENT: 0,
      SELLER_FEE_PERCENTAGE: 0,
      NET_OPERATING_INCOME: 0,
      CLOSE_NOI: 0,
      CAPITALIZATION_RATE: 0,
      NEGOTIATION_CAP_RATE: 0,
      CLOSE_CAP_RATE: 0,
      RENT_PER_SQFT: 0,
      PRICE_PER_SQFT: 0,
      NEGOTIATION_PRICE_PER_SQFT: 0,
      CLOSE_PRICE_PER_SQFT: 0,
    };
    const sums = {
      LIST_PRICE: 0,
      NEGOTIATION_PRICE: 0,
      SOLD_PRICE: 0,
      HSC_PRICE: 0,
      SQFT: 0,
      VACANCY: 0,
      DOWN_PAYMENT: 0,
      SELLER_FEE_PERCENTAGE: 0,
      NET_OPERATING_INCOME: 0,
      CLOSE_NOI: 0,
      CAPITALIZATION_RATE: 0,
      NEGOTIATION_CAP_RATE: 0,
      CLOSE_CAP_RATE: 0,
      RENT_PER_SQFT: 0,
      PRICE_PER_SQFT: 0,
      NEGOTIATION_PRICE_PER_SQFT: 0,
      CLOSE_PRICE_PER_SQFT: 0,
    };
    const items = this.state.items;

    items.forEach((item) => {
      if (
        item.STATUS !== "Active" ||
        (item.STATUS === "Active" && item.LIST_PRICE)
      ) {
        stats.count++;
        for (var i = 0; i < Object.getOwnPropertyNames(stats).length; i++) {
          var eachPropertyValue = item[Object.getOwnPropertyNames(stats)[i]];
          if (eachPropertyValue != null && eachPropertyValue != "") {
            stats[Object.getOwnPropertyNames(stats)[i]]++;
          }
        }
      }
      Object.keys(sums).forEach((key) => {
        sums[key] += Number(item[key]);
      });
    });

    stats.TOTAL_LIST_PRICE = sums.LIST_PRICE;
    stats.TOTAL_NEGOTIATION_PRICE = sums.NEGOTIATION_PRICE;
    stats.TOTAL_SOLD_PRICE = sums.SOLD_PRICE;
    stats.TOTAL_HSC_PRICE = sums.HSC_PRICE;
    stats.TOTAL_SQFT = sums.SQFT;

    if (stats.count) {
      Object.keys(sums).forEach((key) => {
        //stats[key] = sums[key] / stats.count;
        stats[key] = sums[key] / stats[key];
      });
    }

    //this.setState({stats, selectionStats: true});
    this.setState({
      stats,
      selectionStats: true,
      totalCount: Number(this.state.totalCount - removedItems.length),
    });
  }
  /**
   * Every row the current filter matches, not just the page on screen.
   *
   * Resolves to the rows already on screen when the user has narrowed the list
   * by hand: re-querying the filter would mix back in exactly what they took
   * out.
   */
  fetchFullFilteredSet({ strict = false, pageSize = FULL_SET_LIMIT, showBusy = true } = {}) {
    const { items, originalItems } = this.state;

    const listNarrowedByUser =
      items &&
      originalItems &&
      items.length < originalItems.length;

    if (listNarrowedByUser) {
      return Promise.resolve(cloneDeep(items));
    }

    const query = preprocessQueryFilters(
      normalizeListQuery(this.props.location.query),
      this.props.userData
    );
    let page = "properties";
    let propertiesQueryName = V7_PROPERTIES_LIST;
    const isTeamFilter2 = query.hasOwnProperty("TERRITORY_LISTING_AGENT_TEAM") &&
      !["Territory1", "Territory2", "Territory3", "Territory4"].includes(
        query.TERRITORY_LISTING_AGENT_TEAM
      );
    const hasExtendedTerritoryFilter2 =
      query.hasOwnProperty("PRODUCT_TYPE") ||
      query.hasOwnProperty("TERRITORY") ||
      query.hasOwnProperty("TERRITORY_MARKET");

    if (isTeamFilter2 && !hasExtendedTerritoryFilter2) {
      page = "territories";
      propertiesQueryName = V9_PROPERTIES_LIST;
    } else if (isTeamFilter2 || hasExtendedTerritoryFilter2) {
      page = "territories";
      propertiesQueryName = V10_PROPERTIES_LIST;
    }
    // A caller reading the list from inside a popup leaves it alone: the
    // busy flag greys the page behind, which reads as the list reloading
    // when nothing about it is changing.
    const setBusy = (value) => {
      if (showBusy) this.setState({ isBusy: value });
    };

    setBusy(true);

    /*
     * Walked a page at a time rather than asked for in one go. A filter
     * matching a few thousand rows needs more memory server-side than the
     * request is allowed, and the failure is a bare 500 with nothing in the
     * log — so one big ask silently returned the page on screen instead
     * (see fetchFullFilteredSet notes in docs/PROJECT-CONTEXT.md).
     */
    const size = Math.max(1, Math.min(pageSize, FULL_SET_LIMIT));
    const collected = [];

    const fetchPage = (offset) => {
      const options = Listing.getOptions(query, page);
      options.param.limit = size;
      options.param.offset = offset;
      const _options = cloneDeep(options);

      return executeQuery({
        queryName: propertiesQueryName,
        options,
        _options,
        expireTime: 1000,
      }).then((result) => {
        if (!result.success) throw new Error("The list could not be loaded.");

        const rows = result.data || [];
        collected.push.apply(collected, rows);

        // A short page is the last one; the cap is the same one the
        // "select everything matching" offer quotes.
        if (rows.length < size || collected.length >= FULL_SET_LIMIT) {
          return collected;
        }

        return fetchPage(offset + size);
      });
    };

    return fetchPage(0)
      .then((rows) => {
        setBusy(null);
        return rows;
      })
      .catch((error) => {
        setBusy(null);
        // Callers that must know they got the whole list say so; the rest
        // keep the long-standing fallback to the rows on screen.
        if (strict) throw error;
        return this.state.items;
      });
  }

  selectSaleComps() {
    this.fetchFullFilteredSet().then((allItems) => {
      this.setState({ saleCompItems: allItems, comps: "sales" });
    });
  }

  startMailMerge() {
    // Ticked rows win over the filter: the checkbox column is right there, and
    // a user who ticked six properties means those six.
    const selected = this.selectedItems();

    this.setState({ mailMergeError: null, preparingMailMerge: true });

    const chosen = selected.length
      ? Promise.resolve(selected)
      : this.fetchFullFilteredSet();

    chosen.then((allItems) => {
      const propertyIds = (allItems || [])
        .map((item) => item && item.ARCIMEDES_ROW_ID)
        .filter(Boolean)
        .map(String);

      if (!propertyIds.length) {
        this.setState({
          preparingMailMerge: null,
          mailMergeError: "This list has no properties to mail merge.",
        });
        return;
      }

      // "more than", not the fetched count: the list query is itself capped,
      // so that number is a floor rather than the size of the list.
      if (propertyIds.length > MAIL_MERGE_MAX_PROPERTIES) {
        this.setState({
          preparingMailMerge: null,
          mailMergeError:
            `A mail merge covers up to ${MAIL_MERGE_MAX_PROPERTIES.toLocaleString()} properties, and this list has more ` +
            `than that. Narrow the filter, or tick the rows you want and press Mail Merge again.`,
        });
        return;
      }

      writeMailMergeSelection({
        propertyIds,
        query: this.props.location.query,
        capturedAt: new Date().toISOString(),
      });

      browserHistory.push("/mail-merge");
    }).catch((err) => {
      this.setState({
        preparingMailMerge: null,
        mailMergeError: err.message || "Could not read this list.",
      });
    });
  }

  handleChangeDropdown(hscquery, optionType, value, valueKey) {
    hscquery.HSC_TEAM_MEMBERS = hscquery[valueKey];
    const _query = this.state.query;
    _query.HSC_TEAM_MEMBERS = hscquery[valueKey];
    this.setState({ hscquery, query: _query }, () => {
      this.fetch(this.state.query);
    });
  }
  onCloseConfirm(val) {
    if (val) {
      const items = [];
      this.selectedItems()
        .forEach((e) => {
          let obj = {};
          obj["ARCIMEDES_ROW_ID"] = e.ARCIMEDES_ROW_ID;
          obj["CREDITS_AT_CLOSING"] = e.CREDITS_AT_CLOSING;
          obj["HSC_TOTAL_FEE"] = e.HSC_TOTAL_FEE;
          obj["DELETED"] = 1;
          items.push(obj);
        });
      manageData({
        tableCode: PROPERTIES,
        data: items,
      }).then((e) => {
        // Deleting rows changes the sums while the filters stay identical, so the
        // reuse cache would otherwise re-serve the pre-delete header.
        this._aggregateCache = null;
        this.setState({ showConfirm: false, selectedById: new Map(), selectedAll: false }, () => {
          this.fetch(this.props.location.query);
        });
        window.history.replaceState(null, "");
      });
    } else {
      this.setState({ showConfirm: false });
    }
  }

  render() {
    const error = this.props.location.state && this.props.location.state.error;
    const success =
      this.props.location.state && this.props.location.state.success;
    const {
      items,
      originalItems,
      totalCount,
      stats,
      loadingNewList,
      isBusy,
      query,
      checkboxes,
      selectedNo,
      unselectedNo,
      areRemovedItems,
      uniquePropCount,
      preparingMailMerge,
      mailMergeError,
      bulkEditOpen,
      bulkEditResult,
      findDupsOpen,
      mergeOpen,
      mergeProperties,
      mergeResult,
      selectedById,
      selectingAllMatching,
    } = this.state;

    // Everything ticked so far, across the pages walked — not just the page on
    // screen.
    const selectedCount = selectedById.size;
    const mergeBlockedReason = selectedCount < 2
      ? "Select at least 2 properties to merge"
      : selectedCount > MERGE_MAX_PROPERTIES
        ? `A merge takes up to ${MERGE_MAX_PROPERTIES} properties (${selectedCount.toLocaleString()} selected)`
        : null;
    // Every row here is ticked and the filter matches more than fits on a page,
    // so there is a bigger set to offer.
    const canSelectAllMatching =
      items.length > 0 &&
      selectedNo === items.length &&
      Number(totalCount) > selectedCount;

    return (
      <div className="Listing" data-is-busy={isBusy} ref={this.listingRef}>
        <div className="page-rounded-corner-container" ref={this.headerRef}>
          <div className="page-header-container list">
            <ConfirmationDialog
              open={this.state.showConfirm}
              onClose={this.onCloseConfirm}
              content={
                selectedCount === 1
                  ? "Do you really want to delete the selected property?"
                  : `Do you really want to delete the ${selectedCount.toLocaleString()} selected properties?`
              }
            />
            <BulkEditProperties
              open={bulkEditOpen}
              properties={this.selectedItems()}
              onClose={this.closeBulkEdit}
              onSaved={this.onBulkEditSaved}
            />
            <FindDuplicateProperties
              open={findDupsOpen}
              fetchFilteredSet={this.fetchFilteredSetForDups}
              filteredCount={totalCount}
              onClose={this.closeFindDups}
            />
            <MergeProperties
              open={mergeOpen}
              properties={mergeProperties}
              onClose={this.closeMerge}
              onMerged={this.onMerged}
            />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ListStats
                stats={stats}
                loading={loadingNewList}
                query={query}
                uniquePropCount={uniquePropCount}
                items={items}
                newDevelopmentPropertyIds={this.state.newDevelopmentPropertyIds}
                hotListPropertyIds={this.state.hotListPropertyIds}
                needOwnerPropertyIds={this.state.needOwnerPropertyIds}
                territoryFlagCounts={this.state.territoryFlagCounts}
                ownerResearchReviews={this.state.ownerResearchReviews}
                investorHotListCount={this.state.investorHotListCount}
                agentName={this.state.agentName}
                productTypeName={this.state.productTypeName}
                territoryName={this.state.territoryName}
                marketName={this.state.marketName}
                onAgentClick={this.state.initialAgentQuery ? () => {
                  browserHistory.push({ pathname: '/list', query: this.state.initialAgentQuery });
                } : null}
              />
            </div>
            <div
              className="page-header list"
              style={{
                flexDirection: "column",
                alignItems: "flex-start",
                height: "auto",
              }}
            >
              <div
                className="page-title"
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                Properties
                {can('properties.create') && (
                  <LinkComp
                    to={`${arc.path.base}property`}
                    style={{ margin: "10px" }}
                  >
                    <div className="button-new plus positive">
                      <Icon svg={plus} />
                    </div>
                  </LinkComp>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FilterButton onClick={this.props.openFilter} />
                <button
                  type="button"
                  className="ListMapToggle ListMapToggle--active"
                >
                  List
                </button>
                <button
                  type="button"
                  className="ListMapToggle"
                  onClick={() => {
                    // If the user has narrowed the list with Remove Selected / Remove Unselected,
                    // carry those in-memory items over to the map via sessionStorage so the map
                    // shows exactly the same properties without re-fetching from the backend.
                    writeNarrowedHandoff(
                      items.length < originalItems.length ? items : null
                    );
                    browserHistory.push({
                      pathname: "/map",
                      query: this.props.location.query,
                    });
                  }}
                >
                  Map
                </button>
                <button
                  type="button"
                  className="ListMapToggle"
                  title="Parcel boundaries over satellite imagery, with owner details from LightBox"
                  onClick={() => {
                    // Opens on the first row of the current list, carrying the
                    // filters the map can honour, instead of a fixed corner of
                    // Allen. A territory filter opens it on the territory's
                    // outline instead, which the map works out itself.
                    const first = (items || []).find(
                      (item) =>
                        parseFloat(item.LATITUDE) && parseFloat(item.LONGITUDE)
                    );
                    const mapQuery = parcelMapQuery(this.props.location.query);
                    browserHistory.push({
                      pathname: "/map/parcels",
                      query: {
                        ...mapQuery,
                        ...(first && !mapQuery.territory_zip
                          ? {
                              lat: String(first.LATITUDE),
                              lng: String(first.LONGITUDE),
                              zoom: "17",
                            }
                          : {}),
                      },
                    });
                  }}
                >
                  Parcels
                </button>
                <button
                  type="button"
                  className="ListMapToggle"
                  title="Buildings with all their listings and sales grouped together"
                  onClick={() => browserHistory.push({ pathname: "/property-groups" })}
                >
                  Property Groups
                </button>
              </div>
              {(this.props.location.query.SOURCE == "HSC" || this.props.location.query.SOURCE == "PEAK") && (
                <div
                  className="Pipeline_head_right"
                  style={{ margin: "27px 0px", height: "42px" }}
                >
                  <Dropdown
                    dropdownType="multiple"
                    optionsType="team-members"
                    optionLabelKey="NAME"
                    optionKey="EMAIL"
                    value={this.state.hscquery.HSC_TEAM_MEMBERS}
                    label="Agent"
                    valueKey="HSC_TEAM_MEMBERS"
                    data={this.state.hscquery}
                    handleParentChange={this.handleChangeDropdown}
                  />
                </div>
              )}
            </div>
            <div className="line" />
            <div className="buttons-container">
              {(editsEverySummitRecord(getCurrentAccess()) ||
                (editsOwnSummitDealsOnly(getCurrentAccess()) &&
                  this.props.location.query.SOURCE == "External" &&
                  (this.props.location.query.STATUS == "Active" ||
                    this.props.location.query.STATUS == "Closed"))) && (
                <button
                  className={`button-new positive ${
                    !selectedCount ? "inactive" : ""
                  }`}
                  style={{ width: "auto", padding: "0px 20px" }}
                  onClick={this.deleteSelected}
                  title={
                    selectedCount
                      ? `Delete the ${selectedCount.toLocaleString()} selected propert${selectedCount === 1 ? "y" : "ies"}`
                      : "Delete Selected"
                  }
                >
                  <Icon svg={trash} style={{ display: "inline" }} />
                </button>
              )}
              <button
                className={`button-new text ${
                  !selectedNo || selectedNo === items.length ? "inactive" : ""
                }`}
                onClick={this.removeSelected}
              >
                Remove Selected
              </button>
              <button
                className={`button-new text ${
                  !unselectedNo || unselectedNo === items.length
                    ? "inactive"
                    : ""
                }`}
                onClick={this.removeUnselected}
              >
                Remove Unselected
              </button>
              <button
                className={`button-new text ${
                  items.length !== originalItems.length ? "" : "inactive"
                }`}
                onClick={this.reset}
              >
                Reset List
              </button>
              <button
                className={`button-new text ${
                  areRemovedItems ? "" : "inactive"
                }`}
                onClick={this.updateStats}
              >
                Show Page Stats
              </button>
              <button
                className={`button-new text`}
                onClick={this.selectSaleComps}
              >
                Sale Comps
              </button>
              {can('properties.bulk_edit') && (
                <button
                  className="button-new text"
                  onClick={this.openFindDups}
                  title="Find properties recorded more than once"
                >
                  Find Dups
                </button>
              )}
              {can('properties.bulk_edit') && (
                // The title sits on a wrapper because an inactive .button-new.text
                // has pointer-events: none, so its own tooltip never shows.
                <span
                  className="Listing_merge"
                  title={mergeBlockedReason || "Merge the ticked properties into one"}
                >
                  <button
                    className={`button-new text ${mergeBlockedReason ? "inactive" : ""}`}
                    onClick={this.openMerge}
                    disabled={Boolean(mergeBlockedReason)}
                  >
                    {selectedCount
                      ? `Merge (${selectedCount.toLocaleString()})`
                      : "Merge"}
                  </button>
                </span>
              )}
              {can('properties.bulk_edit') && (
                <button
                  className={`button-new text ${!selectedCount ? "inactive" : ""}`}
                  onClick={this.openBulkEdit}
                  disabled={!selectedCount}
                  title="Update the same fields on every ticked property"
                >
                  {selectedCount
                    ? `Bulk Edit (${selectedCount.toLocaleString()})`
                    : "Bulk Edit"}
                </button>
              )}
              {can('mail_merge') && (
                <button
                  className={`button-new text ${preparingMailMerge ? "inactive" : ""}`}
                  onClick={this.startMailMerge}
                  disabled={Boolean(preparingMailMerge)}
                >
                  {preparingMailMerge
                    ? "Preparing…"
                    : selectedCount
                      ? `Mail Merge (${selectedCount.toLocaleString()})`
                      : "Mail Merge"}
                </button>
              )}
            </div>
          </div>
        </div>
        <Print
          comps={this.state.comps}
          location={this.props.location}
          listings={this.state.saleCompItems || items}
          isHscAgent={this.props.userData.email.includes("@highstcapital.com")}
        />
        {error ? <Alert type="error">{error}</Alert> : null}
        {mailMergeError ? <Alert type="error">{mailMergeError}</Alert> : null}
        {bulkEditResult ? (
          <Alert type="success">
            {`Updated ${bulkEditResult.fields.length} field${bulkEditResult.fields.length === 1 ? "" : "s"} on ${bulkEditResult.updated} propert${bulkEditResult.updated === 1 ? "y" : "ies"}.`}
            {bulkEditResult.missing && bulkEditResult.missing.length
              ? ` ${bulkEditResult.missing.length} could not be found and were skipped.`
              : ""}
          </Alert>
        ) : null}
        {mergeResult ? <Alert type="success">{mergeResult}</Alert> : null}
        {success ? <Alert type="success">{success}</Alert> : null}

        <div className="page-body-container">
          {selectedCount ? (
            <div className="Listing_selection">
              <span className="Listing_selection_count">
                {`${selectedCount.toLocaleString()} propert${selectedCount === 1 ? "y" : "ies"} selected`}
                {selectedCount > selectedNo
                  ? ` (${selectedNo.toLocaleString()} on this page)`
                  : ""}
              </span>
              {canSelectAllMatching ? (
                <button
                  type="button"
                  className="Listing_selection_action"
                  onClick={this.selectAllMatching}
                  disabled={selectingAllMatching}
                >
                  {selectingAllMatching
                    ? "Selecting…"
                    : `Select all ${Math.min(Number(totalCount), FULL_SET_LIMIT).toLocaleString()} properties matching this filter`}
                </button>
              ) : null}
              <button
                type="button"
                className="Listing_selection_action"
                onClick={this.clearSelection}
              >
                Clear selection
              </button>
              <span className="Listing_selection_hint">
                Your selection is kept as you page through the list.
              </span>
            </div>
          ) : null}
          <ListTable
            location={this.props.location}
            listings={items}
            everySummitRecord={editsEverySummitRecord(getCurrentAccess())}
            ownDealsOnly={editsOwnSummitDealsOnly(getCurrentAccess())}
            checkboxes={checkboxes}
            toggleCheckbox={this.handleCheckboxChange}
            selectedAll={this.state.selectedAll}
            toggleAllCheckbox={this.handleCheckboxChange}
            lastVisited={this.state.lastVisited}
          />
          <ListResults
            query={this.props.location.query}
            pathname={this.props.location.pathname}
            error={this.state.error}
            totalCount={totalCount}
            itemCount={!loadingNewList && items ? items.length : false}
            originalItemCount={originalItems ? originalItems.length : false}
            limit={limit}
            className="properties"
          />
        </div>
      </div>
    );
  }
}
